// src/pages/productsseller/AddProduct.tsx

import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid"; // UUID 라이브러리 임포트
import { AiOutlineCloudUpload } from "react-icons/ai";
import Step1ProductName from "./Step1ProductName";
import Step2Description from "./Step2Description";
import Step3Category from "./Step3Category";
import Step4EventTheme from "./Step4EventTheme";
import Step5ImageUpload from "./Step5ImageUpload";
import Step6PriceSetting from "./Step6PriceSetting";
import Step7MainFileUpload from "./Step7MainFileUpload";
import { NewProduct } from "./type/NewProduct";
import { FileType } from "./type/FileType"; // FileType 인터페이스 임포트

interface AddProductProps {
  closeModal: () => void;
  onAddProduct: (newProduct: NewProduct) => void;
}

const AddProduct: React.FC<AddProductProps> = ({ closeModal, onAddProduct }) => {
  const [files, setFiles] = useState<FileType[]>([]);
  const [mainFile, setMainFile] = useState<FileType | null>(null); // 메인 파일 상태 추가

  const [category, setCategory] = useState<string>("");
  const [productName, setProductName] = useState<string>("");
  const [description, setDescription] = useState<string>(""); // 새로운 상태 변수 추가
  const [eventTheme, setEventTheme] = useState<string>("basic");
  const [price, setPrice] = useState<number>(0);
  const [discountEnabled, setDiscountEnabled] = useState<boolean>(false);
  const [discountRate, setDiscountRate] = useState<number>(0);
  const [agreeToDiscount, setAgreeToDiscount] = useState<boolean>(false);
  const [applyDiscountToStoreEvents, setApplyDiscountToStoreEvents] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [currentStep, setCurrentStep] = useState<number>(1);

  // 단계별 제목과 설명을 정의하는 객체
  const stepHeaders: { [key: number]: { title: string; description: string } } = {
    1: {
      title: "상품명 입력",
      description: "상품의 이름을 입력해주세요.",
    },
    2: {
      title: "상품 설명",
      description: "상품에 대한 자세한 설명을 작성해주세요.",
    },
    3: {
      title: "카테고리 선택",
      description: "상품의 카테고리를 선택해주세요.",
    },
    4: {
      title: "이벤트 테마 선택",
      description: "상품에 적용할 이벤트 테마를 선택해주세요.",
    },
    5: {
      title: "이미지 업로드",
      description: "상품 이미지를 업로드해주세요.",
    },
    6: {
      title: "가격 설정",
      description: "상품의 가격과 할인 정보를 설정해주세요.",
    },
    7: {
      title: "메인 파일 업로드",
      description: "상품의 메인 파일을 업로드해주세요.",
    },
    // 추가 단계가 있다면 여기에 추가
  };

  // 현재 단계의 헤더 정보 가져오기
  const currentHeader = stepHeaders[currentStep] || {
    title: "상품등록",
    description: "원하는 파일을 선택하여 업로드하세요",
  };

  // Utility function to generate unique IDs for files
  const generateUniqueId = () => {
    return uuidv4(); // UUID 라이브러리 사용
  };

  // 이미지 업로드 핸들러
  const handleMainImageChange = async (newFiles: File[]) => {
    const filesToAdd = newFiles.filter(
      (file) =>
        !files.some(
          (existingFile) =>
            existingFile.name === file.name &&
            existingFile.size === Math.round(file.size / 1024)
        )
    );

    const readFilePromises = filesToAdd.map((file, index) => {
      return new Promise<FileType>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const preview = reader.result as string;
          resolve({
            id: generateUniqueId(),
            name: file.name,
            size: Math.round(file.size / 1024), // Size in KB
            progress: 0,
            status: "uploading",
            isThumbnail: files.length === 0 && index === 0, // Set as thumbnail if it's the first file
            preview,
          });
        };
        reader.onerror = () => {
          reject(new Error("Failed to read file"));
        };
        reader.readAsDataURL(file);
      });
    });

    try {
      const newFileObjects = await Promise.all(readFilePromises);
      setFiles((prevFiles) => {
        // If no files previously, set the first new file as thumbnail
        if (prevFiles.length === 0 && newFileObjects.length > 0) {
          newFileObjects[0].isThumbnail = true;
        }
        return [...prevFiles, ...newFileObjects];
      });
      // Start upload simulation for each new file
      newFileObjects.forEach(simulateUpload);
    } catch (error) {
      console.error("Error processing files:", error);
      setErrors((prevErrors) => ({
        ...prevErrors,
        mainImage: "파일을 처리하는 중 오류가 발생했습니다.",
      }));
    }
  };

  // 업로드 시뮬레이션 함수
  const simulateUpload = (file: FileType) => {
    const uploadDuration = 2000; // Total upload duration in ms
    const uploadInterval = 200; // Interval for progress updates in ms
    const totalSteps = uploadDuration / uploadInterval;
    let currentStepCount = 0;

    const interval = setInterval(() => {
      currentStepCount += 1;
      const progress = Math.min((currentStepCount / totalSteps) * 100, 100);

      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.id === file.id
            ? { ...f, progress, status: progress === 100 ? "completed" : "uploading" }
            : f
        )
      );

      if (progress === 100) {
        clearInterval(interval);
      }
    }, uploadInterval);
  };

  // 파일 제거 핸들러
  const handleRemoveFile = (fileId: string) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((file) => file.id !== fileId);
      const removedFile = prevFiles.find((file) => file.id === fileId);
      if (removedFile?.isThumbnail && updatedFiles.length > 0) {
        updatedFiles[0].isThumbnail = true; // Set the first file as thumbnail
      }
      return updatedFiles;
    });
  };

  // 썸네일 설정 핸들러
  const handleSetThumbnail = (fileId: string) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === fileId
          ? { ...file, isThumbnail: true }
          : { ...file, isThumbnail: false }
      )
    );
  };

  // 메인 파일 업로드 핸들러
  const handleMainFileChange = async (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = reader.result as string;
      const newMainFile: FileType = {
        id: generateUniqueId(),
        name: file.name,
        size: Math.round(file.size / 1024), // Size in KB
        progress: 0,
        status: "uploading",
        isThumbnail: false,
        preview,
      };
      setMainFile(newMainFile);
      simulateMainFileUpload(newMainFile);
    };
    reader.onerror = () => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        mainFile: "메인 파일을 처리하는 중 오류가 발생했습니다.",
      }));
    };
    reader.readAsDataURL(file);
  };

  // 메인 파일 업로드 시뮬레이션 함수
  const simulateMainFileUpload = (file: FileType) => {
    const uploadDuration = 3000; // Total upload duration in ms
    const uploadInterval = 300; // Interval for progress updates in ms
    const totalSteps = uploadDuration / uploadInterval;
    let currentStepCount = 0;

    const interval = setInterval(() => {
      currentStepCount += 1;
      const progress = Math.min((currentStepCount / totalSteps) * 100, 100);

      setMainFile((prevFile) =>
        prevFile
          ? {
            ...prevFile,
            progress,
            status: progress === 100 ? "completed" : "uploading",
          }
          : prevFile
      );

      if (progress === 100) {
        clearInterval(interval);
      }
    }, uploadInterval);
  };

  // 메인 파일 제거 핸들러
  const handleRemoveMainFile = () => {
    setMainFile(null);
  };

  // 유효성 검사 함수
  const validateStep = () => {
    const newErrors: { [key: string]: string } = {};

    if (currentStep === 1) {
      if (!productName.trim()) {
        newErrors.productName = "상품명을 입력해주세요.";
      } else if (productName.length > 50) {
        newErrors.productName = "상품명은 최대 50자까지 입력할 수 있습니다.";
      }
    } else if (currentStep === 2) {
      if (!description.trim()) {
        newErrors.description = "상품 설명을 입력해주세요.";
      } else if (description.length > 1000) {
        newErrors.description = "상품 설명은 최대 1000자까지 입력할 수 있습니다.";
      }
    } else if (currentStep === 3) {
      if (!category) {
        newErrors.category = "카테고리를 선택해주세요.";
      }
    } else if (currentStep === 4) {
      if (!eventTheme) {
        newErrors.eventTheme = "이벤트 테마를 선택해주세요.";
      }
    } else if (currentStep === 5) {
      if (files.length === 0) {
        newErrors.mainImage = "이미지를 업로드해주세요.";
      }
      // 모든 파일의 업로드가 완료되었는지 확인
      const uploadingFiles = files.filter((file) => file.status === "uploading");
      if (uploadingFiles.length > 0) {
        newErrors.mainImage = "모든 파일의 업로드가 완료되어야 합니다.";
      }
    } else if (currentStep === 6) {
      if (price <= 0) {
        newErrors.price = "상품 가격은 0보다 커야 합니다.";
      }
      if (discountEnabled) {
        if (discountRate < 0 || discountRate > 100) {
          newErrors.discountRate = "할인율은 0% 이상 100% 이하이어야 합니다.";
        }
        // 추가적인 검증이 필요하다면 여기에 추가
      }
    } else if (currentStep === 7) {
      if (!mainFile) {
        newErrors.mainFile = "메인 파일을 업로드해주세요.";
      } else if (mainFile.status !== "completed") {
        newErrors.mainFile = "메인 파일의 업로드가 완료되어야 합니다.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;

    // Find the thumbnail image
    const thumbnailFile = files.find((file) => file.isThumbnail);

    const newProduct: NewProduct = {
      image: thumbnailFile ? thumbnailFile.preview : null,
      mainFile: mainFile ? mainFile.preview : null, // 메인 파일 추가
      category,
      name: productName,
      price,

      status: "심사중",
      eventTheme, // Include selected theme
      images: files.map((file) => file.preview), // All images
      description, // 새로운 필드 추가
      discountEnabled, // 할인 적용 여부
      discountRate, // 할인율
      agreeToDiscount, // 크릿 이벤트 할인 동의
    };

    onAddProduct(newProduct);
    resetForm();
  };

  const resetForm = () => {
    setFiles([]);
    setMainFile(null); // 메인 파일 초기화
    setCategory("");
    setProductName("");
    setDescription(""); // 새로운 상태 초기화
    setEventTheme("basic");
    setPrice(0);
    setDiscountEnabled(false);
    setDiscountRate(0);
    setAgreeToDiscount(false);
    setApplyDiscountToStoreEvents(false);
    setErrors({});
    setCurrentStep(1);
  };

  return (
    <div className="w-[600px] flex justify-center items-center">
      <div className="w-full h-full p-4 ">
        {/* Header */}
        <div className="border-b-2 pb-6 flex items-center gap-4">
          <div className="w-[53px] h-[53px] border rounded-full flex justify-center items-center">
            <AiOutlineCloudUpload className="text-[30px] text-[#b9b9b9]" />
          </div>
          <div>
            <h3 className="font-bold flex items-center gap-3 text-2xl text-[#111]">
              {currentHeader.title}
            </h3>
            <span className="font-medium text-sm leading-5 text-[#555]">
              {currentHeader.description}
            </span>
          </div>
        </div>

        {/* Form */}
        <form
          className="mt-4 flex flex-col items-center h-full"
          onSubmit={handleSubmit}
          noValidate
        >
          {/* Step Components */}
          {currentStep === 1 && (
            <Step1ProductName
              productName={productName}
              setProductName={setProductName}
              errors={errors}
            />
          )}

          {currentStep === 2 && (
            <Step2Description
              description={description}
              setDescription={setDescription}
              errors={errors}
            />
          )}

          {currentStep === 3 && (
            <Step3Category
              category={category}
              setCategory={setCategory}
              errors={errors}
            />
          )}

          {currentStep === 4 && (
            <Step4EventTheme
              eventTheme={eventTheme}
              setEventTheme={setEventTheme}
              errors={errors}
            />
          )}

          {currentStep === 5 && (
            <Step5ImageUpload
              files={files}
              handleMainImageChange={handleMainImageChange}
              handleRemoveFile={handleRemoveFile}
              handleSetThumbnail={handleSetThumbnail}
              errors={errors}
            />
          )}

          {currentStep === 6 && (
            <Step6PriceSetting
              price={price}
              setPrice={setPrice}
              discountEnabled={discountEnabled}
              setDiscountEnabled={setDiscountEnabled}
              discountRate={discountRate}
              setDiscountRate={setDiscountRate}
              agreeToDiscount={agreeToDiscount}
              setAgreeToDiscount={setAgreeToDiscount}
              setApplyDiscountToStoreEvents={setApplyDiscountToStoreEvents}
              errors={errors}
            />
          )}

          {currentStep === 7 && (
            <Step7MainFileUpload
              mainFile={mainFile}
              handleMainFileChange={handleMainFileChange}
              handleRemoveMainFile={handleRemoveMainFile} // 추가
              errors={errors}
            />
          )}

          {/* Navigation Buttons */}
          <div className="mt-10 w-full flex justify-between gap-4 py-4">
            {currentStep > 1 && (
              <button
                type="button"
                className="bg-white hover:bg-slate-50 transition duration-300 ease-in-out text-black border px-4 py-2 text-base rounded-lg"
                onClick={handlePrevious}
              >
                이전
              </button>
            )}
            {currentStep < 7 && ( // 총 단계 수 7단계로 수정
              <button
                type="button"
                className={`bg-black/85 hover:opacity-80 transition duration-300 ease-in-out text-white px-4 py-2 text-base rounded-lg ${
                  (currentStep === 1 &&
                    (productName.trim() === "" || productName.length > 50)) ||
                  (currentStep === 2 && !description.trim()) ||
                  (currentStep === 3 && !category) ||
                  (currentStep === 4 && !eventTheme) ||
                  (currentStep === 5 &&
                    (files.length === 0 || files.some((f) => f.status !== "completed"))) ||
                  (currentStep === 6 &&
                    (price <= 0 ||
                      (discountEnabled && (discountRate < 0 || discountRate > 100))))
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                onClick={handleNext}
                disabled={
                  (currentStep === 1 &&
                    (productName.trim() === "" || productName.length > 50)) ||
                  (currentStep === 2 && !description.trim()) ||
                  (currentStep === 3 && !category) ||
                  (currentStep === 4 && !eventTheme) ||
                  (currentStep === 5 &&
                    (files.length === 0 || files.some((f) => f.status !== "completed"))) ||
                  (currentStep === 6 &&
                    (price <= 0 ||
                      (discountEnabled && (discountRate < 0 || discountRate > 100))))
                }
              >
                다음
              </button>
            )}
            {currentStep === 7 && ( // 마지막 단계인 7단계에서 "등록하기" 버튼 표시
              <>
                <button
                  type="submit"
                  className="bg-black/85 hover:opacity-80 transition duration-300 ease-in-out text-white px-4 py-2 rounded-lg"
                >
                  등록하기
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProduct;
